import React, { useEffect, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, Modal, Popconfirm, Row, Select, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelfService } from '../../hooks/useSelfService';
import { useLeave } from '../../hooks/useLeave';
import { LeaveRequest, LeaveStatus } from '../../types';

const STATUS_COLOR: Record<LeaveStatus, string> = {
  PENDING: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  CANCELLED: 'default',
};

const SelfServiceLeaveView: React.FC = () => {
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const { leaveRequests, leaveBalances, loading, fetchMyLeaveRequests, fetchMyLeaveBalances, submitLeaveRequest, cancelMyLeaveRequest } = useSelfService();
  const { leaveTypes, fetchLeaveTypes } = useLeave();

  useEffect(() => {
    fetchMyLeaveRequests();
    fetchMyLeaveBalances();
    fetchLeaveTypes();
  }, []);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    await submitLeaveRequest({
      ...values,
      startDate: values.startDate?.format('YYYY-MM-DD'),
      endDate: values.endDate?.format('YYYY-MM-DD'),
    });
    setModalOpen(false);
    form.resetFields();
  };

  const columns = [
    { title: 'Leave Type', dataIndex: 'leaveTypeName', key: 'leaveTypeName' },
    { title: 'Start Date', dataIndex: 'startDate', key: 'startDate' },
    { title: 'End Date', dataIndex: 'endDate', key: 'endDate' },
    { title: 'Days', dataIndex: 'daysRequested', key: 'daysRequested', width: 60 },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (s: LeaveStatus) => <Tag color={STATUS_COLOR[s]}>{s}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 90,
      render: (_: unknown, record: LeaveRequest) =>
        record.status === 'PENDING' ? (
          <Popconfirm title="Cancel this request?" onConfirm={() => cancelMyLeaveRequest(record.id!)}>
            <Button size="small" danger>Cancel</Button>
          </Popconfirm>
        ) : null,
    },
  ];

  const balanceColumns = [
    { title: 'Leave Type', dataIndex: 'leaveTypeName', key: 'leaveTypeName' },
    { title: 'Entitled', dataIndex: 'entitledDays', key: 'entitledDays', width: 80 },
    { title: 'Taken', dataIndex: 'takenDays', key: 'takenDays', width: 70 },
    { title: 'Pending', dataIndex: 'pendingDays', key: 'pendingDays', width: 80 },
    { title: 'Remaining', dataIndex: 'remainingDays', key: 'remainingDays', width: 90,
      render: (v: number) => <strong style={{ color: v < 3 ? '#ff4d4f' : '#52c41a' }}>{v}</strong>,
    },
  ];

  return (
    <div className="p-6">
      <Row gutter={[16, 16]}>
        {/* Leave Balances */}
        <Col span={24}>
          <Card title="My Leave Balances">
            <Table dataSource={leaveBalances} columns={balanceColumns} rowKey="id" size="small" pagination={false} loading={loading} />
          </Card>
        </Col>

        {/* Leave Requests */}
        <Col span={24}>
          <Card
            title="My Leave Requests"
            extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>Apply for Leave</Button>}
          >
            <Table dataSource={leaveRequests} columns={columns} rowKey="id" size="small" loading={loading} />
          </Card>
        </Col>
      </Row>

      <Modal
        title="Apply for Leave"
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        okText="Submit"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="leaveTypeId" label="Leave Type" rules={[{ required: true }]}>
            <Select placeholder="Select leave type">
              {leaveTypes.filter((t) => t.active).map((t) => (
                <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} disabledDate={(d) => d && d.isBefore(dayjs(), 'day')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endDate" label="End Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} disabledDate={(d) => d && d.isBefore(dayjs(), 'day')} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="Please provide a reason for your leave request" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SelfServiceLeaveView;
